/**
 * React-Query hooks for the `/api/auth/*` endpoints.
 *
 * Each mutation/query owns: the call, the response shape, and the
 * effect on the auth store. Components import these instead of calling
 * `api.post('/auth/login', ...)` directly — keeps the contract typed
 * and the side-effects in one place.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { useAuthStore, type AuthUser } from '@/stores/auth'

interface TokenResponse {
  access_token: string
  refresh_token: string
  token_type: 'bearer'
  expires_in: number
  user: AuthUser
}

interface RegisterBody  { email: string; password: string }
interface LoginBody     { email: string; password: string; rememberMe?: boolean }

// ─── Register ──────────────────────────────────────────────────────────

export function useRegister() {
  const setAuth = useAuthStore((s) => s.setAuth)
  return useMutation({
    mutationFn: async (body: RegisterBody) => {
      const { data } = await api.post<TokenResponse>('/auth/register', body)
      return data
    },
    onSuccess(data) {
      setAuth(
        { access: data.access_token, refresh: data.refresh_token },
        data.user,
      )
    },
  })
}

// ─── Login ─────────────────────────────────────────────────────────────

export function useLogin() {
  const setAuth = useAuthStore((s) => s.setAuth)
  const setRememberMe = useAuthStore((s) => s.setRememberMe)
  return useMutation({
    mutationFn: async (body: LoginBody) => {
      // rememberMe is a client-side preference — don't send it to the API.
      const { data } = await api.post<TokenResponse>('/auth/login', {
        email: body.email,
        password: body.password,
      })
      return data
    },
    onSuccess(data, variables) {
      setRememberMe(variables.rememberMe ?? true)
      setAuth(
        { access: data.access_token, refresh: data.refresh_token },
        data.user,
      )
    },
  })
}

// ─── Me ────────────────────────────────────────────────────────────────

export function useMe(opts: { enabled?: boolean } = {}) {
  const setUser = useAuthStore((s) => s.setUser)
  return useQuery({
    queryKey: ['auth', 'me'],
    enabled: opts.enabled ?? true,
    queryFn: async () => {
      const { data } = await api.get<AuthUser>('/auth/me')
      setUser(data)
      return data
    },
    staleTime: 30_000,
  })
}

// ─── Logout ────────────────────────────────────────────────────────────

export function useLogout() {
  const clear = useAuthStore((s) => s.clear)
  const qc = useQueryClient()
  return () => {
    clear()
    qc.clear()
    window.location.href = '/login'
  }
}

// ─── PULSE-269: сброс пароля ─────────────────────────────────────────

export function useForgotPassword() {
  return useMutation({
    mutationFn: async (email: string) => {
      const { data } = await api.post<{ ok: boolean }>('/auth/forgot', { email })
      return data
    },
  })
}

export function useResetPassword() {
  return useMutation({
    mutationFn: async ({ token, password }: { token: string; password: string }) => {
      const { data } = await api.post<{ ok: boolean }>('/auth/reset', { token, password })
      return data
    },
  })
}
